import React, { useEffect } from "react";

export default function Unauthorized() {
  useEffect(() => {
    // Inject scoped, forced CSS (dark-only iOS-style)
    if (!document.getElementById("ua-styles-dark")) {
      const style = document.createElement("style");
      style.id = "ua-styles-dark";
      style.innerHTML = `
/* ───────────────────── SCOPED / FORCED CSS ───────────────────── */
:root { --ua-font: Inter, "SF Pro Text", -apple-system, Segoe UI, Roboto, system-ui, sans-serif; }

.ua-root{
  min-height:100vh; display:flex; align-items:center; justify-content:center;
  position:relative; overflow:hidden; color:#E6EAF2; background:#05070B;
  font-family:var(--ua-font);
}

/* Dark gradient field (no color; layered graphite) */
.ua-bg{
  position:absolute; inset:0; z-index:0; background:
    radial-gradient(1100px 700px at -10% -20%, rgba(255,255,255,.04), transparent 60%),
    radial-gradient(900px 540px at 120% 0%, rgba(255,255,255,.035), transparent 55%),
    linear-gradient(180deg, #07090F 0%, #0B0F17 55%, #080B12 100%);
}

/* Soft monochrome bloom blobs (no neon) */
.ua-blob{ position:absolute; border-radius:999px; filter:blur(60px); opacity:.32; mix-blend:screen; animation:ua-breathe 12s ease-in-out infinite; }
.ua-blob.a{ width:520px; height:520px; left:-170px; top:-170px;
  background:radial-gradient(closest-side, rgba(255,255,255,.08), rgba(255,255,255,0));
}
.ua-blob.b{ width:460px; height:460px; right:-150px; bottom:-180px; animation-delay:.4s;
  background:radial-gradient(closest-side, rgba(255,255,255,.06), rgba(255,255,255,0));
}
@keyframes ua-breathe{ 0%,100%{ transform:scale(1)} 50%{ transform:scale(1.05)} }

/* Glass card (graphite glass, no borders/lines) */
.ua-card{
  position:relative; z-index:1; width:min(92vw, 440px); border-radius:22px; padding:28px 26px;
  background: rgba(18,22,32,.55); backdrop-filter: blur(16px) saturate(140%); -webkit-backdrop-filter: blur(16px) saturate(140%);
  box-shadow: 0 42px 140px -48px rgba(0,0,0,.85), inset 0 1px 0 rgba(255,255,255,.03);
  isolation:isolate;
}
/* Ambient halo (subtle, grayscale) */
.ua-card:before{
  content:""; position:absolute; inset:-2px; border-radius:24px;
  background: conic-gradient(from 220deg at 50% 50%, rgba(255,255,255,.06), rgba(255,255,255,.02), rgba(255,255,255,.05), rgba(255,255,255,.06));
  filter: blur(26px); opacity:.45; z-index:-1;
}

/* Minimal top light bar (no lines) */
.ua-topglow{
  height:6px; width:100%; border-radius:12px; margin-bottom:14px;
  background: linear-gradient(90deg, rgba(255,255,255,.28), rgba(255,255,255,.12), rgba(255,255,255,.28));
  box-shadow: 0 10px 40px rgba(0,0,0,.55), 0 12px 48px rgba(255,255,255,.06);
}

/* Icon tile (graphite glass) */
.ua-icon{
  width:54px; height:54px; display:grid; place-items:center; margin:0 auto 10px; border-radius:14px;
  background: linear-gradient(135deg, rgba(255,255,255,.10), rgba(255,255,255,.06));
  box-shadow: inset 0 1px 0 rgba(255,255,255,.05), 0 18px 46px -20px rgba(0,0,0,.6);
}
.ua-lock{ color:#F4F7FF; opacity:.92; }

/* Type (compact, pro) */
.ua-h1{ font-size:22px; font-weight:900; letter-spacing:.15px; line-height:1.25; text-align:center; }
.ua-sub{ margin-top:8px; font-size:13.5px; color:#C1C7D3; text-align:center; font-weight:600; }

/* Buttons — graphite only (no colors) */
.ua-actions{ display:flex; gap:10px; justify-content:center; margin-top:16px; flex-wrap:wrap; }
.ua-btn{
  appearance:none; border:none; outline:none; cursor:pointer; padding:11px 14px; border-radius:12px;
  font-weight:800; font-size:14px; letter-spacing:.1px; color:#E9EDF6;
  transition: transform .12s ease, filter .12s ease, box-shadow .2s ease; will-change: transform, filter, box-shadow;
}
.ua-btn:active{ transform:scale(.98); }

/* Primary: subtle graphite gradient with luminous lift */
.ua-primary{
  background: linear-gradient(135deg, #20242C, #171B22);
  box-shadow: 0 14px 36px -16px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.04);
}
.ua-primary:hover{ filter:brightness(1.06); box-shadow: 0 0 0 1px rgba(255,255,255,.06), 0 16px 44px -16px rgba(0,0,0,.8); }

/* Ghost: ultra subtle glass */
.ua-ghost{
  background: rgba(255,255,255,.06);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.03), 0 10px 30px -18px rgba(0,0,0,.7);
}
.ua-ghost:hover{ filter:brightness(1.05); }

/* Hint */
.ua-hint{ margin-top:10px; text-align:center; font-size:12.5px; color:#9AA3B2; }

/* Enter motion */
@keyframes ua-in { from{ opacity:0; transform:translateY(18px) } to{ opacity:1; transform:translateY(0) } }
.ua-in{ animation: ua-in .55s cubic-bezier(.2,.8,.2,1) both; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const goBackSmart = () => {
    try {
      if (window.history.length > 1) { window.history.back(); return; }
      const ref = document.referrer || "";
      if (ref) { window.location.href = ref; } else { window.location.href = "/home"; }
    } catch { window.location.href = "/home"; }
  };

  return (
    <div className="ua-root">
      {/* Dark gradient art */}
      <div className="ua-bg" />
      <div className="ua-blob a" />
      <div className="ua-blob b" />

      {/* Glass card */}
      <section className="ua-card ua-in" role="alert" aria-live="polite">
        <div className="ua-topglow" />
        <div className="ua-icon">
          <svg className="ua-lock" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M7 11V8a5 5 0 0 1 10 0v3" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="5" y="11" width="14" height="10" rx="2.5" ry="2.5"/>
            <path d="M12 15v3" strokeLinecap="round"/>
          </svg>
        </div>

        <h1 className="ua-h1">Access Denied</h1>
        <p className="ua-sub">You don’t have permission to view this page. If this feels unexpected, contact an administrator.</p>

        <div className="ua-actions">
          <button className="ua-btn ua-primary" onClick={() => (window.location.href = "/home")}>
            Go to Home
          </button>
          <button className="ua-btn ua-ghost" onClick={goBackSmart}>
            Go Back
          </button>
        </div>

        <div className="ua-hint">
          Error code: <strong>403</strong>
        </div>
      </section>
    </div>
  );
}
